import { GlobalStateController } from "bai-react-global-state";
import type { User } from "firebase/auth";
import _ from "lodash";
import moment from "moment";
import React from "react";

import { debug as debugApi, workspaces as workspacesApi } from "apis/callables";
import {
    AnalyticsRolesAndLicensesMap,
    BEAUTIFUL_WORKSPACE_ID,
    PusherEventType
} from "common/constants";
import {
    ActionPermissionsObject,
    GET_DEFAULT_BASIC_PLAN,
    GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS,
    GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS,
    IUserStorageModel,
    IWorkspace,
    IWorkspaceDocumentChangedEvent,
    IWorkspaceIntegrationConfig,
    IWorkspaceMetadata,
    IWorkspacePlan,
    IWorkspacePlayerSettings,
    IWorkspaceResourceFolder,
    IWorkspaceUser,
    IWorkspaceUserExtended,
    IWorkspaceUserGroup,
    IWorkspaceUserInvite,
    Optional,
    PermissionActionType,
    TeamMemberRole,
    WorkspaceIntegrationType,
    WorkspacePlanTier,
    WorkspaceResourceType,
    WorkspaceUserRole
} from "common/interfaces";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import { getActionPermissionsCombinedWithPlanPermissions, getCombinedActionPermissions } from "common/utils/workspaces/permissions";
import { isUserGroupLibrarians } from "common/utils/workspaces/userGroups";
import * as analytics from "js/analytics";
import BillingController from "js/controllers/BillingController";
import PresentationLibraryController from "js/controllers/PresentationLibraryController";
import getLogger, { LogGroup } from "js/core/logger";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { download, trackActivity, trackState } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import MandatoryUpdateDialog from "js/react/components/Dialogs/MandatoryUpdateDialog";
import Spinner from "js/react/components/Spinner";

const logger = getLogger(LogGroup.WORKSPACES);

export interface WorkspaceControllerState {
    initialized: boolean;
    initializeError: Error | null;
    workspace: IWorkspace;
    users: IWorkspaceUserExtended[];
    userInvites: IWorkspaceUserInvite[];
    userGroups: IWorkspaceUserGroup[];
    resourceFolders: IWorkspaceResourceFolder[];
    isPastDue: boolean;
    plan: IWorkspacePlan;
}

const initialState: WorkspaceControllerState = {
    initialized: false,
    initializeError: null,
    workspace: null,
    users: [],
    userInvites: [],
    userGroups: [],
    resourceFolders: [],
    isPastDue: false,
    plan: GET_DEFAULT_BASIC_PLAN()
};

class WorkspaceController extends GlobalStateController<WorkspaceControllerState> {
    private _pusherChannel: ExtendedChannel;
    private _pusherChannelUnbind: () => void;
    private _firebaseUser: User;
    private _userModel: IUserStorageModel;
    private _runSequentially: SequentialRunner;

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public get workspaceId() {
        return this._state.workspace?.id;
    }

    public get workspaceUser() {
        return this._state.users.find(user => user.uid === this._firebaseUser.uid);
    }

    public get workspaceUsers() {
        return this._state.users;
    }

    public get workspaceUserGroups() {
        return this._state.userGroups.filter(group => this.workspaceUser?.groupIds.includes(group.id));
    }

    public get role() {
        if (!this.initialized) {
            return WorkspaceUserRole.GUEST;
        }

        return this.workspaceUser.role;
    }

    public get usedSeatCount() {
        return this._state.users.filter(user => user.role !== WorkspaceUserRole.GUEST).length + this._state.userInvites.filter(invite => invite.role !== WorkspaceUserRole.GUEST).length;
    }

    public get legacyRole() {
        if (!this.initialized) {
            return TeamMemberRole.MEMBER;
        }

        return _getUserLegacyRole(this.workspaceUser, this._state.userGroups);
    }

    public get isLibrarian() {
        if (!this.initialized) {
            return false;
        }

        return _getUserLegacyRole(this.workspaceUser, this._state.userGroups) === TeamMemberRole.LIBRARIAN;
    }

    public get isOwner() {
        if (!this.initialized) {
            return false;
        }

        return this.workspaceUser.role === WorkspaceUserRole.OWNER;
    }

    public get actionPermissions() {
        if (!this.initialized) {
            return GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS();
        }

        return getActionPermissionsCombinedWithPlanPermissions(
            getCombinedActionPermissions((this.workspaceUser?.groupIds.map(id => this._state.userGroups.find(group => group.id === id)?.actionPermissions) ?? []).filter(Boolean)),
            this.planPermissions
        );
    }

    public get planPermissions() {
        if (!this.initialized) {
            return GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS();
        }

        return this._state.plan.permissions;
    }

    public get isPastDue() {
        return this._state.isPastDue;
    }

    public get initialized() {
        return this._state.initialized;
    }

    public get plan() {
        return this._state.plan;
    }

    public get resourceFolders() {
        return this._state.resourceFolders;
    }

    /**
     * Helper to check if the workspace is a multi-user workspace (i.e. team or enterprise)
     */
    public get isMultiUserWorkspace() {
        return [WorkspacePlanTier.TEAM, WorkspacePlanTier.ENTERPRISE].includes(this.plan.tier);
    }

    /**
     * Helper to check if the workspace is basic or current user is a guest
     */
    public get isBasicOrGuest() {
        if (!this.initialized) {
            return true;
        }

        return this.workspaceUser?.role === WorkspaceUserRole.GUEST || this.plan.tier === WorkspacePlanTier.BASIC;
    }

    private _initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        return this._runSequentially(async () => {
            await this.reset(false);

            logger.info("[WorkspaceController] initialize()", { workspaceId, uid: firebaseUser.uid });

            try {
                const [workspace, users, userGroups, resourceFolders, plan, userInvites] = await Promise.all([
                    workspacesApi.getWorkspace({ workspaceId }),
                    workspacesApi.getWorkspaceUsers({ workspaceId }),
                    workspacesApi.getWorkspaceUserGroups({ workspaceId }),
                    workspacesApi.getWorkspaceResourceFolders({ workspaceId }),
                    workspacesApi.getWorkspacePlanData({ workspaceId }),
                    workspacesApi.getWorkspaceUserInvites({ workspaceId })
                ]);

                analytics.trackWorkspaceChange(firebaseUser, workspaceId);
                analytics.trackState(_getAnalyticsProps(workspace, users.find(user => user.uid === firebaseUser.uid), userGroups));

                try {
                    localStorage.setItem(BEAUTIFUL_WORKSPACE_ID, workspaceId);
                } catch (err) {
                    logger.error(err, "[WorkspaceController] initialize() failed to set workspaceId in localStorage");
                }

                this._pusherChannel = await pusher.subscribe(`private-legacy-workspace-${workspaceId === "personal" ? firebaseUser.uid : workspaceId}`);
                this._pusherChannelUnbind = this._pusherChannel.bindChunked(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

                this._firebaseUser = firebaseUser;
                this._userModel = userModel;

                await this._updateState({
                    workspace,
                    initialized: true,
                    initializeError: null,
                    users,
                    userGroups,
                    resourceFolders,
                    plan,
                    userInvites
                });

                this._postInitialize();
            } catch (err) {
                logger.error(err, "[WorkspaceController] initialize() failed to initialize");

                await this._updateState({
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    private _postInitialize() {
        // Show dialog if the user's role has changed while offline
        // TODO: this should be handled via a notification
        if (this._userModel.has("workspaceRoleChange")) {
            const roleChangedForWorkspaceId = this._userModel.get("workspaceRoleChange");
            if (roleChangedForWorkspaceId === this.workspaceId) {
                this._onWorkspaceRoleChanged();
            }
        }

        this._checkPastDue();
    }

    private async _checkPastDue() {
        try {
            // @ts-ignore
            const { default: checkPastDue } = await import("js/core/billing/checkPastDue");
            const isPastDue = await checkPastDue(this.workspaceId, this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]);
            if (this.isPastDue !== isPastDue) {
                await this._updateState({ isPastDue });
            }
        } catch (err) {
            logger.error(err, "[WorkspaceController] _checkPastDue() failed to check past due");
        }
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[WorkspaceController] reset()");

            if (this._pusherChannel) {
                this._pusherChannelUnbind();
                if (!this._pusherChannel.isInUse) {
                    pusher.unsubscribe(this._pusherChannel.name);
                }
                this._pusherChannel = null;
                this._pusherChannelUnbind = null;
            }

            await this._updateState(_.cloneDeep(initialState));

            await BillingController.reset();
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }

    protected _stateDidUpdate(prevState: WorkspaceControllerState) {
        const { initialized, users, workspace, userGroups } = this._state;

        if (!initialized) {
            return;
        }

        const handleUsersUpdate = () => {
            if (_.isEqual(prevState.users, users)) {
                return;
            }

            const uid = this._firebaseUser.uid;

            const currentUserBefore = prevState.users.find(user => user.uid === uid);
            const currentUserAfter = this.workspaceUser;

            if (!currentUserBefore || !currentUserAfter) {
                return;
            }

            const roleBefore = _getUserLegacyRole(currentUserBefore, userGroups);
            const roleAfter = this.legacyRole;
            if (roleBefore !== roleAfter) {
                this._onWorkspaceRoleChanged();
            }
        };

        const handleWorkspaceUpdate = () => {
            if (prevState.workspace?.name !== workspace.name) {
                // Force metadata reload to update the name
                this._userModel.loadWorkspacesMetadata()
                    .catch(err => {
                        logger.error(err, "[WorkspaceController] _stateDidUpdate() failed to reload user workspaces metadata");
                    });
            }
        };

        const handleActionPermissionsUpdate = () => {
            if (_.isEqual(prevState.users, users) && _.isEqual(prevState.userGroups, userGroups)) {
                return;
            }

            const uid = this._firebaseUser.uid;
            const currentUserBefore = prevState.users.find(user => user.uid === uid);
            const actionPermissionsBefore = getCombinedActionPermissions(
                (currentUserBefore?.groupIds.map(id => prevState.userGroups.find(group => group.id === id)?.actionPermissions) ?? []).filter(Boolean)
            );

            // If the user has billing access, we need the billing controller to be initialized, otherwise we need to reset it
            if (actionPermissionsBefore[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE] !== this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                if (!this.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                    BillingController.reset();
                    return;
                }

                if (BillingController.initialized) {
                    return;
                }

                this._initializeBillingController();
            }
        };

        handleUsersUpdate();
        handleWorkspaceUpdate();
        handleActionPermissionsUpdate();
    }

    private _initializeBillingController() {
        return BillingController.initialize(this.workspaceId, this._firebaseUser)
            .catch(err => {
                logger.error(err, "[WorkspaceController] _initializeBillingController() failed to initialize BillingController");
            });
    }

    private _onPusherEvent = (documentChangeEvents: IWorkspaceDocumentChangedEvent[]) => {
        this._runSequentially(async () => {
            for (const event of documentChangeEvents) {
                if (event.documentType === "Workspace") {
                    const workspace = event.newDocument as IWorkspace;
                    if (workspace.modifiedAt > this._state.workspace.modifiedAt) {
                        this._updateState({ workspace });
                    }
                }

                if (event.documentType === "WorkspaceUser") {
                    const workspaceUser = event.newDocument as IWorkspaceUserExtended;
                    const uid = event.documentId;

                    if (event.operationType === "create" || event.operationType === "update") {
                        const userBeforeUpdate = this._state.users.find(user => user.uid === uid);
                        if ((userBeforeUpdate?.modifiedAt ?? 0) < workspaceUser.modifiedAt) {
                            this._updateState(state => ({
                                ...state,
                                users: _.cloneDeep([...state.users.filter(user => user.uid !== uid), workspaceUser])
                            }));
                        }
                    } else if (event.operationType === "delete") {
                        const userBeforeDelete = this._state.users.find(user => user.uid === uid);
                        if ((userBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                            this._updateState(state => ({
                                ...state,
                                users: _.cloneDeep(state.users.filter(user => user.uid !== uid))
                            }));
                        }
                    }
                }

                if (event.documentType === "WorkspaceUserGroup") {
                    const workspaceUserGroup = event.newDocument as IWorkspaceUserGroup;
                    const groupId = event.documentId;

                    if (event.operationType === "create" || event.operationType === "update") {
                        const groupBeforeUpdate = this._state.userGroups.find(group => group.id === groupId);
                        if ((groupBeforeUpdate?.modifiedAt ?? 0) < workspaceUserGroup.modifiedAt) {
                            this._updateState(state => ({
                                ...state,
                                userGroups: _.cloneDeep([...state.userGroups.filter(group => group.id !== groupId), workspaceUserGroup])
                            }));
                        }
                    } else if (event.operationType === "delete") {
                        const groupBeforeDelete = this._state.userGroups.find(group => group.id === groupId);
                        if ((groupBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                            this._updateState(state => ({
                                ...state,
                                userGroups: _.cloneDeep(state.userGroups.filter(group => group.id !== groupId))
                            }));
                        }
                    }
                }

                if (event.documentType === "WorkspaceResourceFolder") {
                    const workspaceResourceFolder = event.newDocument as IWorkspaceResourceFolder;
                    const folderId = event.documentId;

                    if (event.operationType === "create" || event.operationType === "update") {
                        const folderBeforeUpdate = this._state.resourceFolders.find(folder => folder.id === folderId);
                        if ((folderBeforeUpdate?.modifiedAt ?? 0) < workspaceResourceFolder.modifiedAt) {
                            this._updateState(state => ({
                                ...state,
                                resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== folderId), workspaceResourceFolder])
                            }));
                        }
                    } else if (event.operationType === "delete") {
                        const folderBeforeDelete = this._state.resourceFolders.find(folder => folder.id === folderId);
                        if ((folderBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                            this._updateState(state => ({
                                ...state,
                                resourceFolders: _.cloneDeep(state.resourceFolders.filter(folder => folder.id !== folderId))
                            }));
                        }
                    }
                }

                if (event.documentType === "WorkspacePlan") {
                    const plan = event.newDocument as IWorkspacePlan;
                    this._updateState({ plan });
                }

                if (event.documentType === "WorkspaceUserInvite") {
                    const inviteId = event.documentId;

                    if (event.operationType === "create" || event.operationType === "update") {
                        const invite = event.newDocument as IWorkspaceUserInvite;
                        const inviteBeforeUpdate = this._state.userInvites.find(i => invite.id === inviteId);
                        if ((inviteBeforeUpdate?.modifiedAt ?? 0) < invite.modifiedAt) {
                            if (invite.acceptedAt) {
                                this._updateState(state => ({
                                    ...state,
                                    userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
                                }));
                            } else {
                                this._updateState(state => ({
                                    ...state,
                                    userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
                                }));
                            }
                        }
                    } else if (event.operationType === "delete") {
                        const inviteBeforeDelete = this._state.userInvites.find(invite => invite.id === inviteId);
                        if ((inviteBeforeDelete?.modifiedAt ?? 0) < event.timestamp) {
                            this._updateState(state => ({
                                ...state,
                                userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
                            }));
                        }
                    }
                }
            }
        });
    }

    private async _onWorkspaceRoleChanged() {
        this._userModel.update({ workspaceRoleChange: null });
        await this._userModel.updatePromise.catch(err => {
            logger.error(err, "[WorkspaceController] _onWorkspaceRoleChanged() failed to clear workspaceRoleChange on user");
        });

        const role = this.legacyRole;
        const workspaceName = this._state.workspace.name;

        let message: string;
        switch (role) {
            case TeamMemberRole.OWNER:
                message = `Your role was changed to Owner in ${workspaceName}. Owners can manage members, Team Slides, and the Team Theme. Please refresh the browser to reflect the latest changes.`;
                break;
            case TeamMemberRole.LIBRARIAN:
                message = `Your role was changed to Librarian in ${workspaceName}. Librarians can manage Team Slides and the Team Theme. Please refresh the browser to reflect the latest changes.`;
                break;
            case TeamMemberRole.MEMBER_LICENSED:
                message = `Your role was changed to Pro Member in ${workspaceName}. Pro Members have access to Beautiful.ai Pro features. Please refresh the browser to reflect the latest changes.`;
                break;
            case TeamMemberRole.MEMBER:
                message = `Your role was changed to Free Team Member in ${workspaceName}. Free Team Members are limited to basic features. Please refresh the browser to reflect the latest changes.`;
                break;
        }

        ShowDialog(MandatoryUpdateDialog, { message });
    }

    public initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        return this._initialize(workspaceId, firebaseUser, userModel);
    }

    public reset(runSequentially = true) {
        return this._reset(runSequentially);
    }

    /**
     * Determines if user's account can be upgraded (either their role or the workspace plan) to remove a restriction on a specific action.
     *
     * Used for determining if an upgrade CTA should be shown. The logic is adjusted to be as close as possible
     * to the legacy logic for determining if an upgrade CTA should be shown.
     *
     * Examples:
     * - User is guest and the action is not allowed for them, but the workspace plan allows the action, meaning upgrading to a paid member will remove the restriction (true is returned)
     * - User is not guest, and the action is restricted by the plan, meaning the restriction can be lifted by upgrading the workspace plan (true is returned)
     */
    public canUpgradeToRemoveActionRestriction(actionObject: ActionPermissionsObject, actionType: PermissionActionType) {
        if (this.actionPermissions[actionObject][actionType]) {
            // Already unrestricted
            return false;
        }

        if (this.role === WorkspaceUserRole.GUEST) {
            if (this.planPermissions[actionObject]) {
                // Guest, means access is restricted by them being a guest,
                // so they can be upgraded
                return true;
            }

            // Access is restricted by the plan
            return false;
        }

        if (!this.planPermissions[actionObject]) {
            // User is not guest, but the action is restricted by the plan
            // so they can be upgraded
            return true;
        }

        return false;
    }

    public getUserLegacyRole(userOrInvite: IWorkspaceUser | IWorkspaceUserInvite) {
        return _getUserLegacyRole(userOrInvite, this._state.userGroups);
    }

    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    public async forceRefreshWorkspace() {
        return this._runSequentially(async () => {
            const [workspace, planData] = await Promise.all([
                workspacesApi.getWorkspace({ workspaceId: this.workspaceId }),
                workspacesApi.getWorkspacePlanData({ workspaceId: this.workspaceId })
            ]);

            await this._updateState({ workspace, ...planData });
        });
    }

    public async forceRefreshWorkspaceResourceFolders(folderIds: string[]) {
        return this._runSequentially(async () => {
            const workspaceResourceFolders = await workspacesApi.getWorkspaceResourceFolders({ workspaceId: this.workspaceId, folderIds });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => !folderIds.includes(folder.id)), ...workspaceResourceFolders])
            }));
        });
    }

    public async updateWorkspace({ name, sso, player }: {
        name?: IWorkspace["name"];
        sso?: Optional<IWorkspace["sso"], keyof IWorkspace["sso"]>
        player?: {
            settings?: Optional<IWorkspacePlayerSettings, keyof IWorkspacePlayerSettings>;
        }
    }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.updateWorkspace({ workspaceId: this.workspaceId, update: { name, sso, player } });

            //// Legacy analytics ////
            if (name) {
                const props = {
                    old_organization_name: workspace.name,
                    new_organization_name: name
                };
                trackState({ "organization_name": name });
                trackActivity("Organization", "NameChanged", null, null, props, { audit: true });
            }
            ////

            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async updateWorkspaceUserGroups({ update }: {
        update: {
            [groupId: string]: {
                actionPermissions: Partial<IWorkspaceUserGroup["actionPermissions"]>;
            };
        }
    }) {
        return this._runSequentially(async () => {
            const userGroups = await workspacesApi.updateWorkspaceUserGroups({ workspaceId: this.workspaceId, update });
            await this._updateState(state => ({
                ...state,
                userGroups: _.cloneDeep([...state.userGroups.filter(group => !userGroups.some(ug => ug.id === group.id)), ...userGroups])
            }));
            return userGroups;
        });
    }

    public async updateWorkspaceResourceFolder({ folderId, name }: { folderId: string, name: string }) {
        return this._runSequentially(async () => {
            const workspaceResourceFolder = await workspacesApi.updateWorkspaceResourceFolder({ workspaceId: this.workspaceId, folderId, name });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders.filter(folder => folder.id !== folderId), workspaceResourceFolder])
            }));
            return workspaceResourceFolder;
        });
    }

    public async deleteWorkspaceResourceFolder({ folderId }: { folderId: string }) {
        return this._runSequentially(async () => {
            await workspacesApi.deleteWorkspaceResourceFolder({ workspaceId: this.workspaceId, folderId });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep(state.resourceFolders.filter(folder => folder.id !== folderId))
            }));
        });
    }

    public async createWorkspaceResourceFolder({ name, resourcesType }: { name: string, resourcesType: WorkspaceResourceType }) {
        return this._runSequentially(async () => {
            const workspaceResourceFolder = await workspacesApi.createWorkspaceResourceFolder({ workspaceId: this.workspaceId, name, resourcesType });
            await this._updateState(state => ({
                ...state,
                resourceFolders: _.cloneDeep([...state.resourceFolders, workspaceResourceFolder])
            }));
            return workspaceResourceFolder;
        });
    }

    public async verifyDomain() {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.verifyWorkspaceDomain({ workspaceId: this.workspaceId });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async parseSamlMetadata({ metadataUrl }: { metadataUrl: string }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.parseSamlMetadata({ workspaceId: this.workspaceId, metadataUrl });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async disconnectWorkspaceIntegration({ integrationType }: { integrationType: WorkspaceIntegrationType }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.disconnectWorkspaceIntegration({ workspaceId: this.workspaceId, integrationType });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async connectWorkspaceIntegration({ integrationType, config }: { integrationType: WorkspaceIntegrationType, config: IWorkspaceIntegrationConfig }) {
        return this._runSequentially(async () => {
            const workspace = await workspacesApi.connectWorkspaceIntegration({ workspaceId: this.workspaceId, integrationType, config });
            await this._updateState({ workspace });
            return workspace;
        });
    }

    public async downloadUsersAndInvitesList() {
        const { users, userInvites } = this._state;

        const lines = [
            "Member,Email,Role,Pending,License,Last Active",
            ...[
                ...users.map(user => ({ ...user, pending: false, displayName: user.displayName ?? user.email })),
                ...userInvites.map(invite => ({ ...invite, pending: true, lastActive: null, displayName: invite.email }))
            ].map(member => ([
                member.displayName,
                member.email,
                this.getUserLegacyRole(member),
                member.pending ? "Pending Invite" : "",
                member.role === WorkspaceUserRole.GUEST ? "free" : "team pro",
                member.lastActive ? moment(member.lastActive).format("YYYY-MM-DD HH:mm") : ""
            ].join(",")))
        ];

        download(`data:text/plain;charset=utf-8,${encodeURIComponent(lines.join("\n"))}`, `members.csv`);
    }

    public async resendUserInvite({ inviteId }: { inviteId: string }) {
        return this._runSequentially(async () => {
            const invite = await workspacesApi.resendWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId });

            //// Legacy analytics ////
            trackActivity("Organization", "ResendTeamInvite", null, null, {
                org_id: this.workspaceId,
                team_id: invite.migrationInfo?.additionalInfo?.teamId,
                license: invite.role === WorkspaceUserRole.GUEST ? "free" : "pro",
                recipient_email: invite.email,
                sender_email: this._firebaseUser.email,
            }, { audit: true });
            ////

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
            }));
        });
    }

    public async copyUserInviteLinkToClipboard({ invite }: { invite: IWorkspaceUserInvite }) {
        return this._runSequentially(async () => {
            const url = await workspacesApi.getWorkspaceUserInviteUrl({
                workspaceId: invite.workspaceId,
                inviteId: invite.id
            });

            await clipboardWrite({
                [ClipboardType.TEXT]: url,
            });

            //// Legacy analytics ////
            const props = {
                team_id: invite.migrationInfo?.additionalInfo?.teamId,
                license: invite.role === WorkspaceUserRole.GUEST ? "free" : "pro",
                recipient_email: invite.email,
                sender_email: this._firebaseUser.email,
                teamInviteUrl: url,
            };
            trackActivity("Organization", "CopyTeamInviteLink", null, null, props, { audit: true });
            ////
        });
    }

    public async deleteUserInvite({ inviteId }: { inviteId: string }) {
        return this._runSequentially(async () => {
            const deletedInvite = await workspacesApi.deleteWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId });

            //// Legacy analytics ////
            trackActivity("Organization", "InviteRemoved", null, null, { email: deletedInvite.email, org_id: this.workspaceId }, { audit: true });
            ////

            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep(state.userInvites.filter(invite => invite.id !== inviteId))
            }));
        });
    }

    public async updateUserInvite({ inviteId, update }: { inviteId: string, update: Pick<IWorkspaceUserInvite, "role"> }) {
        return this._runSequentially(async () => {
            const invite = await workspacesApi.updateWorkspaceUserInvite({ workspaceId: this.workspaceId, inviteId, update });
            await this._updateState(state => ({
                ...state,
                userInvites: _.cloneDeep([...state.userInvites.filter(invite => invite.id !== inviteId), invite])
            }));
        });
    }

    public async inviteWorkspaceUsers(users: Pick<IWorkspaceUserInvite, "email" | "role" | "groupIds">[]) {
        return this._runSequentially(async () => {
            const userInvites = await workspacesApi.inviteWorkspaceUsers({ workspaceId: this.workspaceId, users });
            await this._updateState(state => ({ ...state, userInvites }));
        });
    }

    public async updateWorkspaceUser({ uid, update }: { uid: string, update: Pick<IWorkspaceUser, "role" | "groupIds"> }) {
        return this._runSequentially(async () => {
            const user = await workspacesApi.updateWorkspaceUser({ workspaceId: this.workspaceId, uid, update });
            await this._updateState(state => ({ ...state, users: _.cloneDeep([...state.users.filter(user => user.uid !== uid), user]) }));
        });
    }

    public async deleteWorkspaceUser({ uid, transferOption, targetUid }: { uid: string, transferOption: "other" | "revoke" | "self", targetUid?: string }) {
        return this._runSequentially(async () => {
            const { deletedUser, transferredResourceCounts } = await workspacesApi.deleteWorkspaceUser({ workspaceId: this.workspaceId, uid, transferOption, targetUid });

            await this._updateState(state => ({ ...state, users: _.cloneDeep(state.users.filter(user => user.uid !== uid)) }));

            //// Legacy analytics ////
            const legacyRole = _getUserLegacyRole(deletedUser, this._state.userGroups);
            const props = {
                workspace_id: this.workspaceId,
                affected_userid: uid,
                affected_user_role: AnalyticsRolesAndLicensesMap[legacyRole],
                affected_user_license: AnalyticsRolesAndLicensesMap[deletedUser.role === WorkspaceUserRole.GUEST ? "free" : "team_pro"],
                affected_user_presentations_owned: transferredResourceCounts[WorkspaceResourceType.PRESENTATION] + transferredResourceCounts[WorkspaceResourceType.PRESENTATION_TEMPLATE],
                presentations_transferred_to_personal: transferOption === "self" ? transferredResourceCounts[WorkspaceResourceType.PRESENTATION] : 0,
                presentations_transferred_to_team_member: transferOption === "other" ? transferredResourceCounts[WorkspaceResourceType.PRESENTATION] + transferredResourceCounts[WorkspaceResourceType.PRESENTATION_TEMPLATE] : 0,
                presentation_recipient_user_id: targetUid
            };
            trackActivity("Organization", "UserRemoved", null, null, props, { audit: true });
            ////

            return deletedUser;
        });
    }

    public async printWorkspaceDebugData() {
        try {
            const { url } = await debugApi.getWorkspaceDebugData({ workspaceId: this.workspaceId });

            if (!url) {
                logger.info("[WorkspaceController] printWorkspaceDebugData() no data");
                return;
            }

            window.open(`/admin/debug?url=${encodeURIComponent(url)}`, "_blank");
        } catch (err) {
            logger.error(err, "[WorkspaceController] printWorkspaceDebugData() failed");
        }
    }
}

const workspaceController = new WorkspaceController();

// for lazy import and debug
app.workspaceController = workspaceController;

export default workspaceController;

function _getUserLegacyRole(userOrInvite: IWorkspaceUser | IWorkspaceUserInvite, userGroups: IWorkspaceUserGroup[]) {
    if (userOrInvite.role === WorkspaceUserRole.OWNER) {
        return TeamMemberRole.OWNER;
    } else if (userOrInvite.role === WorkspaceUserRole.GUEST) {
        return TeamMemberRole.MEMBER;
    } else {
        if (userOrInvite.groupIds.map(id => userGroups.find(group => group.id === id)).some(group => group && isUserGroupLibrarians(group))) {
            return TeamMemberRole.LIBRARIAN;
        }

        return TeamMemberRole.MEMBER_LICENSED;
    }
}

function _getAnalyticsProps(workspace: IWorkspace, user: IWorkspaceUser, userGroups: IWorkspaceUserGroup[]) {
    const workspaceId = workspace.id;

    let legacyRole = "none";
    if (workspaceId !== "personal") {
        legacyRole = _getUserLegacyRole(user, userGroups);
    }

    let current_plan = app.user?.analyticsPersonalPlan;
    if (workspaceId !== "personal") {
        if (user.role === WorkspaceUserRole.GUEST) {
            current_plan = "organization-basic";
        } else {
            current_plan = "organization-pro";
        }
    }

    return {
        workspace_id: workspaceId,
        workspace_type: workspaceId === "personal" ? "personal" : "organization",
        organization_name: workspace.name,
        current_plan,
        // Cheap, but inaccurate as PresentationLibraryController may be not initialized in which case
        // it will return zero
        current_slide_count: PresentationLibraryController.getSlideCount(workspaceId),
        role: legacyRole
    };
}

/**
 * Returns analytics props for workspace
 *
 * @param workspaceId The ID of the workspace to get props for
 * @param uid The ID of the user to get props for
 * @returns The analytics props for the workspace
 */
export async function getAnalyticsPropsForWorkspace(workspaceId: string, uid: string) {
    const [workspace, users, userGroups] = await Promise.all([
        workspacesApi.getWorkspace({ workspaceId }),
        workspacesApi.getWorkspaceUsers({ workspaceId, uids: [uid] }),
        workspacesApi.getWorkspaceUserGroups({ workspaceId })
    ]);

    return _getAnalyticsProps(workspace, users[0], userGroups);
}

/**
 * Returns the initial workspace ID to show when the app loads
 *
 * @param workspacesMetadata The list of workspaces metadata
 * @returns The initial workspace ID to show
 */
export function getInitialWorkspaceId(workspacesMetadata: IWorkspaceMetadata[]) {
    const requestedWorkspaceId = localStorage.getItem(BEAUTIFUL_WORKSPACE_ID);
    if (requestedWorkspaceId && workspacesMetadata.some(workspace => workspace.id === requestedWorkspaceId)) {
        // Show requested workspace
        return requestedWorkspaceId;
    }

    if (workspacesMetadata.length > 1) {
        // If in multiple teams, then show the first team
        return workspacesMetadata.find(workspace => workspace.id !== "personal").id;
    }

    // Otherwise, show personal
    return "personal";
}

/**
 * Returns plan permissions for workspace, falling back to restricted permissions if doesn't have access
 *
 * @param workspaceId The ID of the workspace to get permissions for
 * @returns The workspace plan permissions, or restricted permissions if doesn't have access
 */
export async function getPlanPermissionsForWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.planPermissions;
    }

    try {
        const { permissions } = await workspacesApi.getWorkspacePlanData({ workspaceId });
        return permissions;
    } catch (err) {
        // If user has no access to the workspace, return restricted permissions instead of throwing
        if (err.status === 403 || err.status === 401) {
            return GET_DEFAULT_RESTRICTED_PLAN_PERMISSIONS();
        }

        throw err;
    }
}

/**
 * Returns action permissions for workspace, falling back to restricted permissions if doesn't have access
 *
 * @param workspaceId The ID of the workspace to get permissions for
 * @returns The workspace action permissions, or restricted permissions if doesn't have access
 */
export async function getActionPermissionsForWorkspace(workspaceId: string) {
    if (workspaceController.initialized && workspaceController.workspaceId === workspaceId) {
        return workspaceController.actionPermissions;
    }

    try {
        return await workspacesApi.getUserActionPermissions({ workspaceId });
    } catch (err) {
        if (err.status === 403 || err.status === 401) {
            return GET_DEFAULT_RESTRICTED_ACTION_PERMISSIONS();
        }

        throw err;
    }
}
